import { useMemo } from "react";
import { Plus, Trash2, GripVertical, Link as LinkIcon } from "lucide-react";

/**
 * Editable list of installment payments.
 *
 * Props:
 *   installments: [{id,label,amount,due_date,paid,paid_at,method,payment_link}]
 *   total: number (contract total) — used for the validation hint
 *   onChange: (next) => void
 *   onMarkPaid: (instId, paid, method) => void   // optional; admin save flow
 *   showPaidControls: boolean (true on saved contracts)
 */
export default function PaymentScheduleEditor({
  installments = [],
  total = 0,
  onChange,
  onMarkPaid,
  showPaidControls = false,
}) {
  const sum = useMemo(
    () => installments.reduce((acc, i) => acc + (Number(i.amount) || 0), 0),
    [installments]
  );
  const diff = +(Number(total) - sum).toFixed(2);
  const mismatch = Math.abs(diff) > 0.01;

  function update(idx, patch) {
    const next = installments.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange?.(next);
  }
  function addRow() {
    const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    const remaining = Math.max(diff, 0);
    onChange?.([
      ...installments,
      { id, label: `Payment ${installments.length + 1}`, amount: remaining || 0, due_date: "", paid: false, method: "", payment_link: "" },
    ]);
  }
  function removeRow(idx) {
    onChange?.(installments.filter((_, i) => i !== idx));
  }
  function splitEvenly() {
    if (!installments.length || !total) return;
    const each = +(Number(total) / installments.length).toFixed(2);
    const next = installments.map((it, i) => ({
      ...it,
      amount: i === installments.length - 1
        ? +(Number(total) - each * (installments.length - 1)).toFixed(2)
        : each,
    }));
    onChange?.(next);
  }

  return (
    <div data-testid="payment-schedule-editor">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
          Installments · {installments.length}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={splitEvenly}
            disabled={!installments.length || !total}
            data-testid="split-evenly-btn"
            className="text-[10px] uppercase tracking-wider text-white/60 hover:text-[#C9963F] disabled:opacity-30"
          >
            Split evenly
          </button>
          <span className="text-white/20">·</span>
          <button
            type="button"
            onClick={addRow}
            data-testid="add-installment-btn"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C9963F] hover:text-[#A67A2D]"
          >
            <Plus size={12} /> Add Payment
          </button>
        </div>
      </div>

      {installments.length === 0 && (
        <div className="border border-dashed border-white/15 px-6 py-8 text-center text-white/40 text-xs">
          No installments. Click <span className="text-[#C9963F]">Add Payment</span> to build the schedule.
        </div>
      )}

      <div className="space-y-2">
        {installments.map((it, idx) => (
          <div
            key={it.id || idx}
            className={`border ${it.paid ? "border-[#C9963F]/40 bg-[#C9963F]/5" : "border-white/10 bg-[#0a0a0a]"} p-3`}
            data-testid={`installment-row-${idx}`}
          >
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="hidden sm:flex col-span-1 text-white/20 items-center justify-center pb-2">
                <GripVertical size={14} />
              </div>
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Label</label>
                <input
                  className="w-full bg-transparent border-b border-white/15 px-0 py-1.5 focus:border-[#C9963F] focus:outline-none text-white text-sm"
                  value={it.label || ""}
                  onChange={(e) => update(idx, { label: e.target.value })}
                  placeholder="Deposit / 2nd Payment / Final"
                  data-testid={`installment-label-${idx}`}
                />
              </div>
              <div className="col-span-5 sm:col-span-3">
                <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-1.5 focus:border-[#C9963F] focus:outline-none text-white text-sm"
                  value={it.amount ?? 0}
                  onChange={(e) => update(idx, { amount: Number(e.target.value) })}
                  data-testid={`installment-amount-${idx}`}
                />
              </div>
              <div className="col-span-7 sm:col-span-3">
                <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Due Date</label>
                <input
                  type="date"
                  className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-1.5 focus:border-[#C9963F] focus:outline-none text-white text-sm"
                  value={it.due_date || ""}
                  onChange={(e) => update(idx, { due_date: e.target.value })}
                  data-testid={`installment-due-${idx}`}
                />
              </div>
              <div className="col-span-12 sm:col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  data-testid={`installment-remove-${idx}`}
                  className="text-white/30 hover:text-red-400 p-1.5"
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="col-span-12 sm:col-span-11 sm:col-start-2 mt-1">
                <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">
                  <span className="inline-flex items-center gap-1"><LinkIcon size={10} /> Square Payment Link (optional)</span>
                </label>
                <input
                  type="url"
                  className="w-full bg-transparent border-b border-white/10 px-0 py-1.5 focus:border-[#C9963F] focus:outline-none text-white text-xs placeholder-white/20"
                  value={it.payment_link || ""}
                  onChange={(e) => update(idx, { payment_link: e.target.value })}
                  placeholder="Paste your Square payment link (https://square.link/u/...)"
                  data-testid={`installment-link-${idx}`}
                />
              </div>
            </div>

            {showPaidControls && onMarkPaid && (
              <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-3">
                {it.paid ? (
                  <>
                    <span className="text-xs text-[#C9963F]" data-testid={`installment-paid-${idx}`}>
                      ● PAID{it.method ? ` · ${it.method}` : ""}
                      {it.paid_at ? ` · ${new Date(it.paid_at).toLocaleDateString()}` : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => onMarkPaid(it.id, false, "")}
                      data-testid={`installment-mark-unpaid-${idx}`}
                      className="text-[10px] uppercase tracking-wider text-white/50 hover:text-white underline"
                    >
                      Mark unpaid
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-[10px] uppercase tracking-wider text-white/40">Mark as paid:</span>
                    {["Zelle", "Cash", "Card", "Link"].map((m) => (
                      <button
                        type="button"
                        key={m}
                        onClick={() => onMarkPaid(it.id, true, m)}
                        data-testid={`installment-mark-paid-${idx}-${m}`}
                        className="px-2.5 py-1 text-[10px] uppercase tracking-wider border border-white/15 text-white/70 hover:border-[#C9963F] hover:text-[#C9963F]"
                      >
                        {m}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {installments.length > 0 && (
        <div
          className={`mt-3 px-3 py-2 border text-xs flex items-center justify-between ${
            mismatch ? "border-amber-500/40 bg-amber-500/5 text-amber-300" : "border-[#C9963F]/30 bg-[#C9963F]/5 text-[#C9963F]"
          }`}
          data-testid="installment-summary"
        >
          <span>
            Sum of payments: <span className="font-mono">${sum.toFixed(2)}</span>
            <span className="opacity-60"> / Total ${Number(total).toFixed(2)}</span>
          </span>
          {mismatch ? (
            <span data-testid="installment-mismatch">
              {diff > 0 ? `Short by $${diff.toFixed(2)}` : `Over by $${Math.abs(diff).toFixed(2)}`}
            </span>
          ) : (
            <span>Matches total</span>
          )}
        </div>
      )}
    </div>
  );
}
